var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, useEffect, useRef } from 'react';
import { createControllerBox, createEffectHook, useFormEffects, useFieldState, FormEffectHooks, SchemaField, FormPath } from '@formily/react-schema-renderer';
import { Tabs, Badge } from 'antd';
import { createMatchUpdate } from '../shared';
var StateMap;
(function (StateMap) {
    StateMap["ON_FORM_TAB_ACTIVE_KEY_CHANGE"] = "onFormTabActiveKeyChange";
})(StateMap || (StateMap = {}));
var onFormChange$ = FormEffectHooks.onFormChange$;
var EffectHooks = {
    onTabActiveKeyChange$: createEffectHook(StateMap.ON_FORM_TAB_ACTIVE_KEY_CHANGE)
};
var parseTabItems = function (items, hiddenKeys) {
    return items.reduce(function (buf, _a) {
        var schema = _a.schema, key = _a.key;
        if (Array.isArray(hiddenKeys)) {
            if (hiddenKeys.includes(key)) {
                return buf;
            }
        }
        if (schema.getExtendsComponent() === 'tabpane') {
            return buf.concat({
                props: schema.getExtendsComponentProps(),
                schema: schema,
                key: key
            });
        }
        return buf;
    }, []);
};
var parseDefaultActiveKey = function (hiddenKeys, items, defaultActiveKey) {
    if (hiddenKeys === void 0) { hiddenKeys = []; }
    if (!hiddenKeys.includes(defaultActiveKey))
        return defaultActiveKey;
    var index = items.findIndex(function (item) { return !hiddenKeys.includes(item.key); });
    return index >= 0 ? items[index].key : '';
};
var parseChildrenErrors = function (errors, target) {
    return errors.filter(function (_a) {
        var path = _a.path;
        return FormPath.parse(path).includes(target);
    });
};
var addErrorBadge = function (tab, currentPath, childrenErrors) {
    var currentErrors = childrenErrors.filter(function (_a) {
        var path = _a.path;
        return FormPath.parse(path).includes(currentPath);
    });
    if (currentErrors.length > 0) {
        return (React.createElement(Badge, { offset: [12, 0], count: currentErrors.length }, tab));
    }
    return tab;
};
export var FormTab = createControllerBox('tab', function (_a) {
    var form = _a.form, schema = _a.schema, name = _a.name, path = _a.path;
    var orderProperties = schema.getOrderProperties();
    var _b = schema.getExtendsComponentProps(), hiddenKeys = _b.hiddenKeys, defaultActiveKey = _b.defaultActiveKey, componentProps = __rest(_b, ["hiddenKeys", "defaultActiveKey"]);
    hiddenKeys = hiddenKeys || [];
    var _c = useFieldState({
        activeKey: parseDefaultActiveKey(hiddenKeys, orderProperties, defaultActiveKey),
        childrenErrors: []
    }), _d = _c[0], activeKey = _d.activeKey, childrenErrors = _d.childrenErrors, setFieldState = _c[1];
    var itemsRef = useRef([]);
    itemsRef.current = parseTabItems(orderProperties, hiddenKeys);
    var update = function (cur) {
        form.notify(StateMap.ON_FORM_TAB_ACTIVE_KEY_CHANGE, {
            name: name,
            path: path,
            value: cur
        });
    };
    var matchUpdate = createMatchUpdate(name, path);
    useEffect(function () {
        if (Array.isArray(hiddenKeys)) {
            setFieldState({
                activeKey: parseDefaultActiveKey(hiddenKeys, orderProperties, defaultActiveKey)
            });
        }
    }, [hiddenKeys.length]);
    useFormEffects(function (_a) {
        var hasChanged = _a.hasChanged;
        onFormChange$().subscribe(function (formState) {
            var errorsChanged = hasChanged(formState, 'errors');
            if (errorsChanged) {
                setFieldState({
                    childrenErrors: parseChildrenErrors(formState.errors, path)
                });
            }
        });
        EffectHooks.onTabActiveKeyChange$().subscribe(function (_a) {
            var _b = _a === void 0 ? {} : _a, value = _b.value, name = _b.name, path = _b.path;
            if (!itemsRef.current.map(function (item) { return item.key; }).includes(value))
                return;
            matchUpdate(name, path, function () {
                setFieldState({
                    activeKey: value
                });
            });
        });
    });
    return (React.createElement(Tabs, __assign({}, componentProps, { activeKey: activeKey, onChange: update }), itemsRef.current.map(function (_a) {
        var props = _a.props, schema = _a.schema, key = _a.key;
        var currentPath = FormPath.parse(path).concat(key);
        return (React.createElement(Tabs.TabPane, __assign({}, props, { tab: activeKey === key
                ? props.tab
                : addErrorBadge(props.tab, currentPath, childrenErrors), key: key, forceRender: true }),
            React.createElement(SchemaField, { path: currentPath, schema: schema, onlyRenderProperties: true })));
    })));
});
FormTab.TabPane = createControllerBox('tabpane', function (_a) {
    var children = _a.children;
    return React.createElement(Fragment, null, children);
});
Object.assign(FormTab, StateMap, EffectHooks);
export default FormTab;



//////////////////
// WEBPACK FOOTER
// ./node_modules/@formily/antd-components/esm/form-tab/index.js
// module id = 6612
// module chunks = 0